import React, { useState, useEffect } from 'react';
import { crmAPI, financeAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw, ChevronRight, ChevronLeft, Check } from 'lucide-react';

const Lifts = () => {
    const [lifts, setLifts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [liftBrands, setLiftBrands] = useState([]);
    const [liftTypes, setLiftTypes] = useState([]);
    const [chartAccounts, setChartAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Stepper state
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 4;
    
    const [formData, setFormData] = useState({
        // Step 1: Basic Info
        name: '',
        project_id: '',
        lift_brand_id: '',
        lift_type_id: '',
        // Step 2: Specifications
        capacity: '',
        stop: '',
        opening: '',
        speed: '',
        // Step 3: Billing Info
        monthly_rate: '',
        item_code: '',
        location: '',
        quantity: 1,
        // Step 4: Accounting
        chart_of_account_id: '',
        // Common
        status: 'active'
    });

    useEffect(() => { 
        fetchLifts(); 
        fetchProjects();
        fetchLiftBrands();
        fetchLiftTypes();
        fetchChartAccounts();
    }, []);

    const fetchLifts = async () => {
        try {
            setLoading(true);
            const response = await crmAPI.getLifts();
            // Handle paginated response - Laravel paginator returns: { data: { data: [...], ... } }
            const responseData = response.data;
            const liftsData = responseData?.data?.data || responseData?.data || responseData || [];
            setLifts(Array.isArray(liftsData) ? liftsData : []);
        } catch (err) { 
            console.error('Failed to load lifts:', err);
            setError('Failed to load data: ' + (err.response?.data?.message || err.message));
            setLifts([]);
        }
        finally { setLoading(false); }
    };

    const fetchProjects = async () => {
        try {
            const response = await crmAPI.getProjects();
            setProjects(response.data.data?.data || response.data.data || []);
        } catch (err) {
            console.error('Failed to load projects');
        }
    };

    const fetchLiftBrands = async () => {
        try {
            const response = await crmAPI.getLiftBrands();
            setLiftBrands(response.data.data?.data || response.data.data || []);
        } catch (err) {
            console.error('Failed to load lift brands');
        }
    };

    const fetchLiftTypes = async () => {
        try {
            const response = await crmAPI.getLiftTypes();
            setLiftTypes(response.data.data?.data || response.data.data || []);
        } catch (err) {
            console.error('Failed to load lift types');
        }
    };

    const fetchChartAccounts = async () => {
        try {
            const response = await financeAPI.getChartOfAccounts();
            setChartAccounts(response.data.data?.data || response.data.data || []);
        } catch (err) {
            console.error('Failed to load chart of accounts');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                project_id: formData.project_id || null,
                lift_brand_id: formData.lift_brand_id || null,
                lift_type_id: formData.lift_type_id || null,
                monthly_rate: formData.monthly_rate ? parseFloat(formData.monthly_rate) : null,
                quantity: formData.quantity ? parseInt(formData.quantity) : 1,
                stop: formData.stop || null,
                chart_of_account_id: formData.chart_of_account_id || null,
            };
            
            if (editingId) await crmAPI.updateLift(editingId, submitData);
            else await crmAPI.createLift(submitData);
            setShowModal(false);
            resetForm();
            fetchLifts();
        } catch (err) { 
            setError(err.response?.data?.message || 'Failed to save'); 
        }
    };

    const handleEdit = (lift) => {
        // Handle both nested relations and direct IDs
        const brandId = lift.brand?.id || lift.lift_brand_id || '';
        const typeId = lift.type?.id || lift.lift_type_id || '';
        const projectId = lift.project?.id || lift.project_id || '';
        const accountId = lift.chartAccount?.id || lift.chart_of_account_id || '';
        
        setFormData({
            name: lift.name || '',
            project_id: projectId,
            lift_brand_id: brandId,
            lift_type_id: typeId,
            capacity: lift.capacity || '',
            stop: lift.stop || '',
            opening: lift.opening || '',
            speed: lift.speed || '',
            monthly_rate: lift.monthly_rate || '',
            item_code: lift.item_code || '',
            location: lift.location || '',
            quantity: lift.quantity || 1,
            chart_of_account_id: accountId,
            status: lift.status || 'active'
        });
        setEditingId(lift.id);
        setCurrentStep(1); // Start from step 1 when editing
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try { 
            await crmAPI.deleteLift(id); 
            fetchLifts(); 
        }
        catch (err) { 
            setError('Failed to delete'); 
        }
    };

    const resetForm = () => { 
        setFormData({
            name: '',
            project_id: '',
            lift_brand_id: '',
            lift_type_id: '',
            capacity: '',
            stop: '',
            opening: '',
            speed: '',
            monthly_rate: '',
            item_code: '',
            location: '',
            quantity: 1,
            chart_of_account_id: '',
            status: 'active'
        }); 
        setEditingId(null); 
        setCurrentStep(1);
    };

    const nextStep = () => {
        if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const filteredLifts = lifts.filter(l => 
        l.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-BD', { 
            style: 'currency', 
            currency: 'BDT',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Stepper components
    const StepIndicator = () => (
        <div className="flex items-center justify-center mb-6">
            {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                        currentStep >= step 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-200 text-gray-500 dark:bg-slate-600 dark:text-gray-300'
                    }`}>
                        {currentStep > step ? <Check className="w-4 h-4" /> : step}
                    </div>
                    {step < totalSteps && (
                        <div className={`w-16 h-1 mx-2 ${
                            currentStep > step 
                                ? 'bg-indigo-600' 
                                : 'bg-gray-200 dark:bg-slate-600'
                        }`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    const getStepTitle = () => {
        switch(currentStep) {
            case 1: return 'Basic Information';
            case 2: return 'Specifications';
            case 3: return 'Billing Information';
            case 4: return 'Accounting';
            default: return '';
        }
    };

    const renderStepContent = () => {
        switch(currentStep) {
            case 1:
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Lift Name <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                value={formData.name} 
                                onChange={(e) => setFormData({...formData, name: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                required 
                                placeholder="e.g., U # 1&2 Boiler Passenger Elevators"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Project
                            </label>
                            <select 
                                value={formData.project_id} 
                                onChange={(e) => setFormData({...formData, project_id: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                            >
                                <option value="">Select Project</option>
                                {projects.map(project => (
                                    <option key={project.id} value={project.id}>{project.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Brand
                                </label>
                                <select 
                                    value={formData.lift_brand_id} 
                                    onChange={(e) => setFormData({...formData, lift_brand_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                >
                                    <option value="">Select Brand</option>
                                    {liftBrands.map(brand => (
                                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Type
                                </label>
                                <select 
                                    value={formData.lift_type_id} 
                                    onChange={(e) => setFormData({...formData, lift_type_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                >
                                    <option value="">Select Type</option>
                                    {liftTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                            </label>
                            <select 
                                value={formData.status} 
                                onChange={(e) => setFormData({...formData, status: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Capacity (kg)
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.capacity} 
                                    onChange={(e) => setFormData({...formData, capacity: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    placeholder="e.g., 1500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Stops
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.stop} 
                                    onChange={(e) => setFormData({...formData, stop: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    placeholder="e.g., 9"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Opening
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.opening} 
                                    onChange={(e) => setFormData({...formData, opening: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    placeholder="e.g., Front, Rear"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Speed (m/s)
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.speed} 
                                    onChange={(e) => setFormData({...formData, speed: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    placeholder="e.g., 1.0"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Monthly Rate (Tk.)
                            </label>
                            <input 
                                type="number" 
                                value={formData.monthly_rate} 
                                onChange={(e) => setFormData({...formData, monthly_rate: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                placeholder="e.g., 12000"
                                step="0.01"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Item Code
                            </label>
                            <input 
                                type="text" 
                                value={formData.item_code} 
                                onChange={(e) => setFormData({...formData, item_code: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                placeholder="e.g., 10307000004"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Location
                            </label>
                            <input 
                                type="text" 
                                value={formData.location} 
                                onChange={(e) => setFormData({...formData, location: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                placeholder="e.g., U#1 Boiler, CHP Crusher House"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Quantity
                            </label>
                            <input 
                                type="number" 
                                value={formData.quantity} 
                                onChange={(e) => setFormData({...formData, quantity: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                min="1"
                            />
                        </div>
                        
                        {/* Summary */}
                        {formData.monthly_rate && formData.quantity && (
                            <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900 rounded-lg">
                                <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2">Billing Summary</h4>
                                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                                    Monthly: {parseFloat(formData.monthly_rate || 0).toLocaleString()} Tk × {formData.quantity} = 
                                    <span className="font-bold"> {(parseFloat(formData.monthly_rate || 0) * (formData.quantity || 1)).toLocaleString()} Tk</span>
                                </p>
                            </div>
                        )}
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Chart of Account
                            </label>
                            <select 
                                value={formData.chart_of_account_id} 
                                onChange={(e) => setFormData({...formData, chart_of_account_id: e.target.value})} 
                                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                            >
                                <option value="">Select Chart of Account</option>
                                {chartAccounts.map(account => (
                                    <option key={account.id} value={account.id}>
                                        {account.account_code} - {account.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Link this lift to a specific income/expense account</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Lifts</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage lifts & billing</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Lift
                </button>
            </div>
            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search lifts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Project</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Brand/Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Capacity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Monthly Rate</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredLifts.length > 0 ? filteredLifts.map(lift => (
                                    <tr key={lift.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">{lift.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lift.project?.name || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                            {lift.brand?.name || '-'} / {lift.type?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lift.capacity ? `${lift.capacity}kg` : '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lift.location || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                            {lift.monthly_rate ? formatCurrency(lift.monthly_rate) : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${lift.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {lift.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleEdit(lift)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(lift.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No lifts found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* Modal with Stepper */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-100">
                            {editingId ? 'Edit' : 'Add'} Lift
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Step {currentStep} of {totalSteps}: {getStepTitle()}</p>
                        
                        <StepIndicator />
                        
                        <form onSubmit={handleSubmit}>
                            {renderStepContent()}
                            
                            <div className="flex justify-between gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={() => { setShowModal(false); resetForm(); }} 
                                    className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <div className="flex gap-2">
                                    {currentStep > 1 && (
                                        <button 
                                            type="button" 
                                            onClick={prevStep} 
                                            className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 flex items-center"
                                        >
                                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                                        </button>
                                    )}
                                    {currentStep < totalSteps ? (
                                        <button 
                                            type="button" 
                                            onClick={nextStep} 
                                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center"
                                        >
                                            Next <ChevronRight className="w-4 h-4 ml-1" />
                                        </button>
                                    ) : (
                                        <button 
                                            type="submit" 
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                                        >
                                            {editingId ? 'Update' : 'Create'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Lifts;
